import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from '../components/Logo';
import { api, formatApiError } from '../lib/api';
import { googleSignIn } from '../components/AuthCallback';
import { Eye, EyeOff } from 'lucide-react';
import { suggestCorrectEmail } from '../lib/emailValidation';
import { getStoredReferralCode, clearStoredReferral } from '../lib/referral';
import { usePageSEO } from '../lib/usePageSEO';

/**
 * Family-only signup.
 *
 * The combined family/partner form has been split into two flows:
 *   • /register           → families creating a memorial (this page)
 *   • /become-a-partner   → funeral homes, cemeteries, regional distributors
 *
 * To keep any old share links / emails working, anyone landing here with a
 * partner intent (?as=regional_partner | ?as=memorial_partner | etc.) is
 * gently redirected to the dedicated partner experience.
 */
export default function Register() {
  usePageSEO({
    title: 'Create a memorial — MyLeaf Legacy',
    description: 'Begin a beautiful, permanent memorial for someone you love. Premium digital memorial with QR plaque, photos, life stories, and a private Family Vault.',
    path: '/register',
  });
  const [sp] = useSearchParams();
  const nav = useNavigate();

  // Redirect partner intents to the dedicated /become-a-partner flow.
  useEffect(() => {
    const v = (sp.get('as') || '').toLowerCase();
    if (
      v === 'regional_partner' ||
      v === 'regional' ||
      v === 'memorial_partner' ||
      v === 'professional' ||
      v === 'memorial' ||
      v === 'partner'
    ) {
      const invite = sp.get('invite') || '';
      const intent = (v === 'regional_partner' || v === 'regional') ? 'regional_partner' : 'memorial_partner';
      const qs = new URLSearchParams({ as: intent });
      if (invite) qs.set('invite', invite);
      nav(`/become-a-partner?${qs.toString()}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Form state — family fields only.
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [plan, setPlan] = useState(sp.get('plan') || 'memorial');
  const [plans, setPlans] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  // Referral capture — supports /register?ref=ABC123 or ?partner=ABC123,
  // with a localStorage fallback (iter101) so attribution survives page
  // refreshes, multi-page browsing, and links that landed on other pages.
  // The code is the partner's unique invite/referral code. Once validated
  // server-side, we show a "You'll be enrolled through [Partner Name]"
  // banner and pass `partner_referral_code` to /api/auth/register.
  const initialRef = (
    sp.get('ref') || sp.get('partner') || sp.get('partner_code') || ''
  ).toUpperCase().replace(/[^A-Z0-9]/g, '') || getStoredReferralCode();
  const [referralCode] = useState(initialRef);
  const [referralPartner, setReferralPartner] = useState(null);

  // iter80 — Trackable invite token. If `?inv=...&kind=...` is present,
  // we validate it server-side and lock the inviter on submit. For
  // `kind=memorial_partner` we redirect into the dedicated FH/partner
  // flow so the right form is shown.
  const inviteToken = (sp.get('inv') || '').trim();
  const inviteKind = (sp.get('kind') || '').toLowerCase();
  const [inviteInfo, setInviteInfo] = useState(null);

  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();

  useEffect(() => {
    api.get('/packages')
      .then(({ data }) => {
        setPlans(data.packages || []);
        if (!(data.packages || []).some((p) => p.id === plan)) {
          setPlan((data.packages || [])[0]?.id || 'memorial');
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!referralCode) return;
    api.get(`/partners/referral/${referralCode}`)
      .then(({ data }) => setReferralPartner(data?.valid ? data : { valid: false }))
      .catch(() => setReferralPartner({ valid: false }));
  }, [referralCode]);

  // iter80/iter103 — Validate the trackable invite token server-side
  // (source of truth for the kind — URL param can't be trusted). Only
  // redirect to the FH/partner form when the invite really is an MP
  // invite AND the visitor didn't explicitly request the family form
  // (`kind=family` escape hatch from BecomeAPartner).
  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/invites/validate?token=${encodeURIComponent(inviteToken)}`)
      .then(({ data }) => {
        if (data?.valid && data.invite_kind === 'memorial_partner' && inviteKind !== 'family') {
          const qs = new URLSearchParams({ as: 'memorial_partner', inv: inviteToken });
          nav(`/become-a-partner?${qs.toString()}`, { replace: true });
          return;
        }
        setInviteInfo(data);
      })
      .catch(() => setInviteInfo({ valid: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill email/name from the invite once validated.
  useEffect(() => {
    if (inviteInfo?.valid) {
      if (inviteInfo.target_email && !email) setEmail(inviteInfo.target_email);
      if (inviteInfo.target_name && !name) setName(inviteInfo.target_name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteInfo]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setLoading(true);
    try {
      await register({
        name,
        email,
        phone,
        password,
        plan,
        account_type: 'family',
        // iter80 — trackable invite token wins over user-typed code.
        invite_token: inviteInfo?.valid ? inviteToken : undefined,
        // iter101 — ALWAYS forward the code; the backend is the attribution
        // authority (it also resolves legacy pre-iter89 codes) and silently
        // ignores genuinely invalid ones. Gating on the client-side banner
        // check silently dropped valid legacy attributions.
        partner_referral_code: referralCode || undefined,
      });
      clearStoredReferral();
      nav('/dashboard/new', { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-5">
      <div className="md:col-span-2 relative hidden md:flex flex-col justify-between p-10 bg-[hsl(var(--primary))] text-[#FDFBF7]">
        <Link to="/" className="inline-flex" data-testid="register-side-logo-link">
          <Logo tone="cream" size={176} />
        </Link>
        <div>
          <div className="ml-overline !text-[#D9B775] !font-semibold">Begin a memorial</div>
          <h1 className="font-serif text-5xl mt-4 leading-tight font-semibold">
            A home on the internet, for the people you love.
          </h1>
          <p className="mt-5 text-[#FDFBF7]/90 max-w-md font-medium">
            Create your account, choose a plan, and invite the rest of your family to help build the story together.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-[#FDFBF7]/90 font-medium">
            <li>· A quiet public memorial page with a QR plaque</li>
            <li>· A private Family Vault with request-based access</li>
            <li>· An olive family tree across generations</li>
            <li>· Optional, respectful AI Memory Moments</li>
          </ul>
        </div>
        <div className="text-xs text-[#FDFBF7]/70 font-medium">© MyLeaf Legacy — Forever Here</div>
      </div>

      <div className="md:col-span-3 p-6 sm:p-10 flex items-center">
        <div className="w-full max-w-xl mx-auto">
          <div className="md:hidden mb-8"><Link to="/"><Logo size={128} /></Link></div>
          <div className="ml-overline !text-[hsl(var(--primary))] !font-semibold">Create your account</div>
          <h2 className="font-serif text-4xl mt-2 text-[hsl(var(--primary))] font-semibold">Create a memorial.</h2>
          <p className="mt-3 text-[14.5px] text-[hsl(var(--primary)/0.7)] font-light leading-relaxed">
            For families honouring someone they love.
          </p>

          <button
            onClick={() => googleSignIn('/dashboard/new')}
            className="mt-8 w-full ml-btn-outline !py-3 !justify-center"
            data-testid="register-google-btn"
          >
            <svg viewBox="0 0 48 48" className="w-4 h-4"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.6 32.2 29.2 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.8 7 29.1 5 24 5 13.5 5 5 13.5 5 24s8.5 19 19 19 19-8.5 19-19c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.3 19 13 24 13c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.8 7 29.1 5 24 5 16.3 5 9.7 9 6.3 14.7z"/><path fill="#4CAF50" d="M24 43c5 0 9.7-1.9 13.2-5.1l-6.1-5.1C29 34.1 26.6 35 24 35c-5.2 0-9.6-2.8-11.3-7l-6.5 5C9.6 39 16.2 43 24 43z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.7l6.1 5.1C40.9 35.9 43 30.6 43 24c0-1.2-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>

          <div className="ml-divider-leaf my-6 text-xs">or with email</div>

          <form onSubmit={submit} className="space-y-4 wizard-readable" data-testid="family-register-form">
            {inviteInfo?.valid && (
              <div
                className="rounded-md border border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/12 p-4 flex items-start gap-3"
                data-testid="register-invite-banner"
              >
                <div className="w-2 h-2 rounded-full bg-[hsl(var(--accent))] mt-1.5 shrink-0" />
                <div className="text-[13.5px] leading-relaxed text-[hsl(var(--primary))]">
                  You were personally invited by{' '}
                  <strong>{inviteInfo.inviter_business_name || 'a MyLeaf Partner'}</strong>.
                  Your account will be enrolled under them automatically.
                </div>
              </div>
            )}
            {!inviteInfo?.valid && referralPartner?.valid && (
              <div
                className="rounded-md border border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/12 p-4 flex items-start gap-3"
                data-testid="register-referral-banner"
              >
                <div className="w-2 h-2 rounded-full bg-[hsl(var(--accent))] mt-1.5 shrink-0" />
                <div className="text-[13.5px] leading-relaxed text-[hsl(var(--primary))]">
                  You'll be enrolled through{' '}
                  <strong data-testid="register-referral-partner-name">
                    {referralPartner.partner_name}
                  </strong>
                  <span className="text-[hsl(var(--primary))]/60">
                    {' · '}{referralPartner.partner_type}
                  </span>
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="ml-label">Admin Full Name</label>
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="ml-input"
                  placeholder="Your full name — the person creating this account"
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <label className="ml-label">Phone (optional)</label>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="ml-input"
                  data-testid="register-phone-input"
                />
              </div>
            </div>
            <div>
              <label className="ml-label">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="ml-input"
                data-testid="register-email-input"
              />
              {(() => {
                const sug = suggestCorrectEmail(email);
                if (!sug) return null;
                return (
                  <div className="mt-1.5 text-xs text-amber-700 flex items-center gap-1.5" data-testid="register-email-typo-hint">
                    <span>Did you mean</span>
                    <button
                      type="button"
                      onClick={() => setEmail(sug)}
                      className="font-semibold underline underline-offset-2 hover:text-amber-900"
                      data-testid="register-email-typo-fix"
                    >{sug}</button>
                    <span>?</span>
                  </div>
                );
              })()}
            </div>
            <div>
              <label className="ml-label">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="ml-input !pr-10"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-[hsl(var(--primary)/0.55)] hover:text-[hsl(var(--primary))] transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="register-password-toggle"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                </button>
              </div>
            </div>

            <div>
              <label className="ml-label">Choose a memorial plan</label>
              <div className="grid sm:grid-cols-2 gap-3">
                {plans.map((p) => {
                  const selected = plan === p.id;
                  return (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => setPlan(p.id)}
                      data-testid={`register-plan-${p.id}`}
                      className={`text-left rounded-md border p-4 transition ${
                        selected
                          ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-[#F4E5C2]'
                          : 'border-border hover:border-[hsl(var(--primary))]'
                      }`}
                    >
                      <div className="text-sm font-semibold">{p.name}</div>
                      <div
                        className={`text-xs mt-2 font-mono tracking-wide ${
                          selected ? 'text-[#D9B775]' : 'text-[hsl(var(--primary))]'
                        }`}
                      >
                        ${Number(p.price_setup ?? p.price)} one-time
                        <span className={`block mt-0.5 ${selected ? 'text-[#F4E5C2]/70' : 'text-[hsl(var(--muted-foreground))]'}`}>
                          + ${p.keeping_fee ?? p.price_annual}/year keeping fee
                        </span>
                      </div>
                      <div
                        className={`text-[11px] mt-2 leading-relaxed ${
                          selected ? 'text-[#F4E5C2]/85' : 'text-[hsl(var(--muted-foreground))]'
                        }`}
                      >
                        {p.tagline}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
                You'll choose how to pay after creating the memorial.
              </p>
            </div>

            {err && <div className="text-sm text-[hsl(var(--destructive))]" data-testid="register-error">{err}</div>}

            <button
              type="submit"
              disabled={loading}
              className="ml-btn-primary w-full !py-3.5"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating your memorial…' : 'Begin a memorial'}
            </button>
          </form>

          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-6">
            Already have an account? <Link to="/login" className="text-[hsl(var(--primary))] font-medium" data-testid="register-login-link">Sign in →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
